import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';

type HoverHandlers = {
  onPointerEnter: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  /** Hover-opened popovers leave focus where it is, so a passing mouse never interrupts typing. */
  keepsFocus: () => boolean;
  rememberTrigger: (el: HTMLElement) => void;
  onFocusInside: () => void;
  onCloseAutoFocus: (e: Event) => void;
};

const HoverContext = React.createContext<HoverHandlers | null>(null);

type PopoverState = {
  open: boolean;
  /** Id of the rendered trigger, which names the content unless the caller names it. */
  triggerId: string | undefined;
  setTriggerId: (id: string | undefined) => void;
};

const PopoverContext = React.createContext<PopoverState>({ open: false, triggerId: undefined, setTriggerId: () => {} });

type PopoverProps = React.ComponentProps<typeof PopoverPrimitive.Root> & {
  /**
   * Also open when the mouse rests on the trigger, and close when it leaves both the trigger and the content.
   * Click, keyboard and Escape work as usual; a click on a hover-opened popover keeps it open until the next
   * click, Escape or outside click. Touch and pen ignore hover.
   */
  openOnHover?: boolean;
  openDelay?: number;
  closeDelay?: number;
};

function Popover({
  openOnHover = false,
  openDelay = 400,
  closeDelay = 100,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;
  const timer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  // True while the popover is open only because of hover, so leaving closes it and a click pins it instead.
  const openedByHover = React.useRef(false);
  // Outlives openedByHover through the close, so the close also leaves focus alone.
  const keepFocus = React.useRef(false);
  // Set when focus enters a hover-opened popover, so closing focuses the trigger; Radix would restore whatever had focus before the hover.
  const usedInside = React.useRef(false);
  const triggerEl = React.useRef<HTMLElement | null>(null);
  const [triggerId, setTriggerId] = React.useState<string>();
  React.useEffect(() => () => clearTimeout(timer.current), []);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!next) openedByHover.current = false;
      if (openProp === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  const hover = React.useMemo<HoverHandlers | null>(() => {
    if (!openOnHover) return null;
    return {
      onPointerEnter: (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer.current);
        if (open) return;
        timer.current = setTimeout(() => {
          openedByHover.current = true;
          keepFocus.current = true;
          setOpen(true);
        }, openDelay);
      },
      onPointerLeave: (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer.current);
        if (!openedByHover.current) return;
        timer.current = setTimeout(() => setOpen(false), closeDelay);
      },
      onClick: (e) => {
        clearTimeout(timer.current);
        if (open && openedByHover.current) {
          openedByHover.current = false;
          // Skips Radix's toggle, which would close what the user is reaching for.
          e.preventDefault();
        }
      },
      keepsFocus: () => keepFocus.current,
      rememberTrigger: (el) => {
        triggerEl.current = el;
      },
      onFocusInside: () => {
        if (keepFocus.current) usedInside.current = true;
      },
      onCloseAutoFocus: (e) => {
        if (!keepFocus.current) return;
        e.preventDefault();
        // Only when focus would otherwise be lost (Escape, a click on nothing focusable); a click into
        // another field keeps focus there.
        const active = document.activeElement;
        const lost = !active || active === document.body || (e.target instanceof Node && e.target.contains(active));
        if (usedInside.current && lost) triggerEl.current?.focus();
        keepFocus.current = false;
        usedInside.current = false;
      },
    };
  }, [openOnHover, open, openDelay, closeDelay, setOpen]);

  const state = React.useMemo<PopoverState>(() => ({ open, triggerId, setTriggerId }), [open, triggerId]);

  return (
    <PopoverContext.Provider value={state}>
      <HoverContext.Provider value={hover}>
        <PopoverPrimitive.Root
          data-slot="popover"
          open={open}
          onOpenChange={(next) => {
            // Radix opens only on click or keyboard, which should move focus as usual.
            if (next) {
              keepFocus.current = false;
              usedInside.current = false;
            }
            setOpen(next);
          }}
          {...props}
        />
      </HoverContext.Provider>
    </PopoverContext.Provider>
  );
}

function PopoverTrigger({
  ref,
  onPointerEnter,
  onPointerLeave,
  onClick,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  const hover = React.useContext(HoverContext);
  const { open, setTriggerId } = React.useContext(PopoverContext);
  const fallbackId = React.useId();
  // Reads the id from the element, since an `asChild` child's own id replaces the fallback.
  const syncId = React.useCallback(
    (el: HTMLButtonElement) => {
      setTriggerId(el.id || undefined);
      return () => {
        setTriggerId(undefined);
      };
    },
    [setTriggerId],
  );
  const triggerRef = useComposedRefs(ref, syncId);
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      id={fallbackId}
      // Radix points aria-controls at the content even while it is unmounted.
      {...(open ? null : { 'aria-controls': undefined })}
      ref={triggerRef}
      onPointerEnter={(e) => {
        onPointerEnter?.(e);
        hover?.rememberTrigger(e.currentTarget);
        hover?.onPointerEnter(e);
      }}
      onPointerLeave={(e) => {
        onPointerLeave?.(e);
        hover?.onPointerLeave(e);
      }}
      onClick={(e) => {
        onClick?.(e);
        hover?.onClick(e);
      }}
      {...props}
    />
  );
}

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent(
  {
    className,
    align = 'center',
    sideOffset = 4,
    onPointerEnter,
    onPointerLeave,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onFocusCapture,
    ...props
  },
  ref,
) {
  const hover = React.useContext(HoverContext);
  const { triggerId } = React.useContext(PopoverContext);
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        data-slot="popover-content"
        // An aria-labelledby would override the caller's aria-label, so it is only the fallback name.
        aria-labelledby={props['aria-label'] ? undefined : triggerId}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-white text-primary border border-grey-30 rounded-sm p-3 gap-2',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'z-50 w-auto origin-(--radix-popover-content-transform-origin) shadow-md outline-hidden',
          // Shrinks to the room left beside the trigger, so it stays on screen at 400% zoom.
          'max-w-(--radix-popover-content-available-width)',
          className,
        )}
        // Moving onto the content keeps a hover-opened popover open.
        onPointerEnter={(e) => {
          onPointerEnter?.(e);
          hover?.onPointerEnter(e);
        }}
        onPointerLeave={(e) => {
          onPointerLeave?.(e);
          hover?.onPointerLeave(e);
        }}
        onFocusCapture={(e) => {
          onFocusCapture?.(e);
          hover?.onFocusInside();
        }}
        onOpenAutoFocus={(e) => {
          onOpenAutoFocus?.(e);
          if (hover?.keepsFocus()) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          onCloseAutoFocus?.(e);
          hover?.onCloseAutoFocus(e);
        }}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

/** Points the popover at its trigger; render inside `PopoverContent`. Overlaps it by 1px to hide its border. */
function PopoverArrow({
  className,
  width = 12,
  height = 6,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Arrow>) {
  return (
    <PopoverPrimitive.Arrow data-slot="popover-arrow" width={width} height={height} asChild {...props}>
      <svg viewBox="0 0 12 6" className={cn('-translate-y-px overflow-visible', className)} aria-hidden="true">
        {/* Keeps a 1px outline at any size. */}
        <path d="M0 0 L6 6 L12 0" className="fill-white stroke-grey-30" vectorEffect="non-scaling-stroke" />
      </svg>
    </PopoverPrimitive.Arrow>
  );
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverArrow, type PopoverProps };
